import React, { useCallback, useEffect, useState } from "react";

import EmptyState from "../../components/EmptyState";
import PageHead from "../../components/PageHead";
import * as api from "./api";
import OfferPicker from "./OfferPicker";
import { Panel } from "./panels";

/**
 * Workstation · Audience — first-party performance from our own ad spend.
 *
 * SPLIT FROM RESEARCH 2026-09-09 (RESEARCH-DECISION-BRIEF-BUILD).
 * `WORKSTATION_RESEARCH.md` §5 already excluded audience statistics from that
 * page — "a different data source (our own ads, not third-party research)".
 * Research is what the market says. This is what happened when we spent money.
 *
 * WHAT THIS PAGE REFUSES TO DO, AND WHY
 * =====================================
 * It does not rank on conversions. Measured 2026-09-09: the whole collection
 * holds **230 conversions across 5,818 rows**, and the best-covered age segment
 * is 65+ at $423 spend and zero conversions. Ranking on that would manufacture a
 * winner. Every ranking here names its measure — click-through rate — so nobody
 * reads "top segment" as "top converting segment".
 *
 * It also respects Meta's $25 classification floor (`WORKSTATION_AUDIENCE.md`
 * §4.2: "a spend floor, not a filter… the page must respect that distinction and
 * not rank on unclassified rows"). Unclassified segments still show their spend,
 * marked as not yet rankable.
 *
 * And an offer with no rows says **"we have not spent here"** — never "this
 * audience did not respond". 36 of 42 offers are in that state today.
 */

const DIMENSION_LABEL = {
  age: "Age",
  gender: "Gender",
  placement: "Placement",
  device_os: "Device OS",
  device_type: "Device type",
};

function pct(v) {
  return v === null || v === undefined ? "—" : `${(v * 100).toFixed(2)}%`;
}
function money(v) {
  return v === null || v === undefined ? "—" : `$${Number(v).toLocaleString()}`;
}

/** Data age, stated. The screen used to show six-day-old numbers as if current. */
function Freshness({ freshness }) {
  const f = freshness || {};
  const bits = [];
  if (f.meta_age_days !== null && f.meta_age_days !== undefined) {
    bits.push(`Meta breakdowns: ${f.meta_age_days === 0 ? "today" : `${f.meta_age_days} day(s) old`}`);
  }
  if (f.tracking_age_days !== null && f.tracking_age_days !== undefined) {
    bits.push(`tracking platform: ${f.tracking_age_days === 0 ? "today" : `${f.tracking_age_days} day(s) old`}`);
  }
  if (!bits.length) return null;
  return (
    <div className="dim" style={{ marginBottom: 10, fontSize: "var(--f-label, 10.5px)" }}>
      {bits.join(" · ")}
    </div>
  );
}

/** The conclusions, said out loud. A nine-fold difference between two rows of a
 *  table is only a finding if somebody notices it. */
function Findings({ findings }) {
  if (!findings || !findings.length) return null;
  return (
    <Panel title="What this says" dominant>
      <ul className="ws-list">
        {findings.map((f, i) => (
          <li key={i} className="ws-understanding-item" style={{ display: "block", marginBottom: 8 }}>
            <span className="ws-tag" data-kind={f.kind}>
              {f.kind === "data_fault" ? "data fault" : f.segment_type}
            </span>{" "}
            <strong>{f.headline}</strong>
            {f.detail ? <div className="dim">{f.detail}</div> : null}
          </li>
        ))}
      </ul>
    </Panel>
  );
}

/** Conversions, revenue and rate by segment — the fields Meta does not carry. */
function MoneyPanel({ money }) {
  if (!money) return null;
  if (!money.has_data) {
    return (
      <Panel title="Conversions and revenue" subtitle="from the tracking platform">
        <div className="state ws-state" role="status">{money.reason}</div>
      </Panel>
    );
  }
  const t = money.totals || {};
  return (
    <>
      <Panel
        title="Conversions and revenue"
        subtitle={`${t.conversions ?? 0} conversions · $${Number(t.revenue || 0).toLocaleString()} · ${(t.clicks || 0).toLocaleString()} clicks`}
        dominant
      >
        {Object.entries(money.segments || {}).map(([stype, segs]) => (
          <div key={stype} style={{ marginBottom: 12 }}>
            <div className="ws-kpi-label">{stype}</div>
            <div className="ws-table-scroll">
              <table className="tbl ws-table">
                <thead>
                  <tr>
                    <th>Segment</th><th>Clicks</th><th>Conv.</th>
                    <th>Conv. rate</th><th>Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {segs.slice(0, 12).map((sg) => (
                    <tr key={sg.value}>
                      <td>
                        {sg.display}
                        {sg.platform ? <span className="dim"> · {sg.platform}</span> : null}
                      </td>
                      <td>{(sg.clicks || 0).toLocaleString()}</td>
                      <td>{sg.conversions || 0}</td>
                      <td>
                        {sg.conversion_rate ? `${(sg.conversion_rate * 100).toFixed(2)}%` : "—"}
                        {/* A rate off a handful of clicks is noise, and is
                            labelled rather than quietly printed. */}
                        {sg.conversion_rate && !sg.rate_reliable ? (
                          <span className="ws-tag" title="Too few clicks for this rate to mean anything">
                            thin
                          </span>
                        ) : null}
                      </td>
                      <td>${Number(sg.revenue || 0).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </Panel>

      {money.faults && money.faults.length ? (
        <Panel title="Broken tracking" subtitle="unfilled macros — not real placements">
          <ul className="ws-list">
            {money.faults.map((f, i) => (
              <li key={i} className="ws-understanding-item" style={{ display: "block" }}>
                <code>{f.value}</code> — {f.conversions} conversion(s),
                ${Number(f.revenue || 0).toLocaleString()} on {(f.clicks || 0).toLocaleString()} clicks.
                <div className="dim">
                  The tracking template was never substituted, so this money cannot
                  be attributed anywhere real. Excluded from the rankings above.
                </div>
              </li>
            ))}
          </ul>
        </Panel>
      ) : null}
    </>
  );
}

function Totals({ totals, conversionRanking, note }) {
  return (
    <Panel title="What we have spent, and what it bought" dominant>
      <div className="ws-analytics-grid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(120px,1fr))" }}>
        {[
          ["Spend", money(totals.spend)],
          ["Impressions", (totals.impressions || 0).toLocaleString()],
          ["Clicks", (totals.clicks || 0).toLocaleString()],
          ["CTR", pct(totals.ctr)],
          ["Conversions", totals.conversions ?? 0],
          ["Rows above $25", `${totals.classified_rows ?? 0} of ${totals.rows ?? 0}`],
        ].map(([label, value]) => (
          <div className="ws-kpi" key={label}>
            <div className="ws-kpi-label">{label}</div>
            <div className="ws-kpi-value">{value}</div>
          </div>
        ))}
      </div>

      {/* The honest headline: what this page will NOT tell you. */}
      {!conversionRanking ? (
        <div className="state ws-state" role="status" style={{ marginTop: 12 }}>
          <strong>Segments below are ranked by click-through rate, not conversions.</strong>
          <div style={{ marginTop: 4 }}>{note}</div>
        </div>
      ) : null}
    </Panel>
  );
}

function Dimension({ name, data }) {
  const segments = data.segments || [];
  if (!segments.length) return null;
  const leader = data.leader;

  return (
    <Panel
      title={DIMENSION_LABEL[name] || name}
      subtitle={
        leader
          ? `Leads on ${data.leader_by.replace(/_/g, " ")}: ${leader.value} (${pct(leader.ctr)})`
          : "No segment clears the $25 floor yet — nothing here can be called a leader."
      }
    >
      <div className="ws-table-scroll">
        <table className="tbl ws-table">
          <thead>
            <tr>
              <th>Segment</th>
              <th>Spend</th>
              <th>Impressions</th>
              <th>Clicks</th>
              <th>CTR</th>
              <th>Conv.</th>
            </tr>
          </thead>
          <tbody>
            {segments.map((s) => (
              <tr key={s.value} data-leader={leader && s.value === leader.value ? "true" : undefined}>
                <td>
                  {s.value}
                  {/* Meta's floor, shown rather than hidden: below it a row is
                      stored but cannot be called a winner or a loser yet. */}
                  {!s.rankable ? (
                    <span className="ws-tag" title="Below Meta's $25 classification floor — stored, but not yet rankable">
                      not yet rankable
                    </span>
                  ) : null}
                </td>
                <td>{money(s.spend)}</td>
                <td>{(s.impressions || 0).toLocaleString()}</td>
                <td>{(s.clicks || 0).toLocaleString()}</td>
                <td>{pct(s.ctr)}</td>
                <td>{s.conversions || 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

function AudienceBody({ offerId }) {
  const [state, setState] = useState({ loading: true, data: null, error: null });

  const load = useCallback(async () => {
    if (!offerId) return;
    setState((s) => ({ ...s, loading: true, error: null }));
    const res = await api.getOfferAudience(offerId);
    setState({
      loading: false,
      data: res.ok ? res.data : null,
      error: res.ok ? null : res.error || "request failed",
    });
  }, [offerId]);

  useEffect(() => {
    load();
  }, [load]);

  if (state.loading) return <div className="state ws-state" role="status">Loading audience…</div>;
  if (state.error) {
    return (
      <div className="state ws-state" role="alert">
        Could not load audience data: {state.error}
      </div>
    );
  }

  const d = state.data || {};

  if (!d.has_data) {
    /* An absence of spend, not a finding about the audience. These are
       different facts and must never render the same way. */
    return <EmptyState title="No spend data for this offer yet" detail={d.reason} />;
  }

  return (
    <>
      <Freshness freshness={d.freshness} />
      <div className="ws-analytics-grid">
      <Findings findings={d.findings} />
      <MoneyPanel money={d.money} />
      <Totals
        totals={d.totals || {}}
        conversionRanking={d.conversion_ranking_available}
        note={d.measure_note}
      />

      {Object.entries(d.dimensions || {}).map(([name, data]) => (
        <Dimension key={name} name={name} data={data} />
      ))}

      {d.thin && d.thin.length ? (
        <Panel title="What is still too thin to call">
          <ul className="ws-list">
            {d.thin.map((t, i) => (
              <li key={i} className="dim">{t}</li>
            ))}
          </ul>
        </Panel>
      ) : null}
      </div>
    </>
  );
}

export default function Audience({ selection }) {
  const { offers, selected, selectedId, select, error } = selection;

  return (
    <>
      <PageHead
        title="Audience"
        subtitle={
          selected
            ? `Who actually responded to ${selected.name || "this offer"}'s ads — from our own spend, not from research.`
            : "Who responds, where, and what is still too thin to call."
        }
      />

      <OfferPicker offers={offers} selectedId={selectedId} onSelect={select} error={error} />

      {!selectedId ? (
        <div className="state ws-state" role="status">
          Select an offer to see who responded to its ads.
        </div>
      ) : (
        <AudienceBody offerId={selected?.offer_id} />
      )}
    </>
  );
}
